import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    backgroundimage = "backgroundimage",
    image = "image",
    heading = "heading",
    quoteOwner = "quoteOwner",
    quoteDesignation = "quoteDesignation",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.heading]: string;
        [fields.quoteOwner]: string;
        [fields.quoteDesignation]: string;
    };
    extendedStyles?: CENLeadQuoteStyles;
}

export interface CENLeadQuoteStyles {
    quoteWrapper?: InjectableCss;
    quoteHeading?: InjectableCss;
    quoteImageWrapper?: InjectableCss;
    quoteAuthorWrapper?: InjectableCss;
    quoteDesignationWrapper?: InjectableCss;
}

export const CENleadQuoteStyles: CENLeadQuoteStyles = {
    quoteWrapper: {
        css: css`
            display: flex;
            margin: 33px 48px 21px 32px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin: 16px;
                        display: flex;
                        flex-direction: column;
                        justify-content:center;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    quoteHeading: {
        css: css`
            font-weight: 500 !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        font-weight: ${TYPOGRAPHY.weight.baseBold};
                        font-size: 32px;
                        line-height: 40px;
                        text-align: center;
                        justify-content: center;
                        margin-bottom: 32px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    quoteImageWrapper: {
        css: css`
            margin-right: 28px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0 auto 61px;
                        justify-content: center;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    quoteAuthorWrapper: {
        css: css`
            margin-top: 16px;

            h6 {
                margin-bottom: 4px !important;
                margin-top: 0 !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        font-size:${TYPOGRAPHY.size.base};
                        line-height:${TYPOGRAPHY.lineHeight.base};
                        justify-content: center;
                        margin: auto;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    quoteDesignationWrapper: {
        css: css`
            font-weight:${TYPOGRAPHY.weight.base}!important;
            margin-top: 0 !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-weight: ${TYPOGRAPHY.weight.base};
                        font-size: ${TYPOGRAPHY.size.base};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                        text-align: center;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = CENleadQuoteStyles;
type Props = WidgetProps;

export const CENLeadCaptureQuote: FC<Props> = ({ fields }) => {
    const backgroundStyle = `background-image: url(${fields.backgroundimage});`;

    const wrapper = {
        css: css`
            font-size: ${TYPOGRAPHY.size.base} !important;
            width: auto;
            height: auto;
            margin: auto;
            display: flex;
            align-items: center;
            flex-direction: column;
            ${backgroundStyle};
            background-size: 100% 100% !important;
            backgroud-position: center !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        height: auto;
                        width: auto;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    };
    return (
        <StyledWrapper {...wrapper}>
            <StyledWrapper {...styles.quoteWrapper}>
                <StyledWrapper {...styles.quoteImageWrapper}>
                    <Img className="CENImg" altText={fields.heading} src={fields.image} loading="lazy" />
                </StyledWrapper>
                <StyledWrapper>
                    <Typography variant="h3" {...styles.quoteHeading}>
                        {translate("{0}",fields.heading)}
                    </Typography>
                    <StyledWrapper css={css`display:flex;flex-direction:column;justify-content:center;`}>
                    <StyledWrapper {...styles.quoteAuthorWrapper}>
                        <Typography variant="h6">{translate("{0}",fields.quoteOwner)}</Typography>
                    </StyledWrapper>
                    <Typography variant="h6" {...styles.quoteDesignationWrapper}>
                        {translate("{0}",fields.quoteDesignation)}
                    </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const LeadCaptureQuote: WidgetModule = {
    component: CENLeadCaptureQuote,
    definition: {
        group: "Common",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                name: fields.backgroundimage,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Background Image",
            },
            {
                name: fields.image,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Quote Image",
            },
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.quoteOwner,
                displayName: "Quote Owner",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                fieldType: "Translatable",
                name: fields.quoteDesignation,
                editorTemplate: "TextField",
                displayName: "Quote Designation",
                defaultValue: "",
            },
        ],
    },
};

export default LeadCaptureQuote;
